/**
 * 统一内容配置文件
 * 包含联想问题库和敏感词库配置
 */

import sensitiveConfig from './sensitiveConfig.json';
import suggestionConfig from './suggestionConfig.json';

import logger from '@/utils/logger';

// 联想问题接口
export interface SuggestionQuestion {
  text: string;
  id?: string; // 可选的唯一标识符
}

// 敏感词过滤配置接口
export interface FilterConfig {
  enabled: boolean;
  maskChar: string;
  strictMode: boolean;
  logSensitive: boolean;
  filterUserOnly?: boolean; // 是否只过滤用户消息
  // 新增：日志与降噪控制
  logThrottleMs?: number; // 同一签名限频
  logOnlyFinal?: boolean; // 仅在最终字幕/消息时记录
  aggregateSensitiveLogs?: boolean; // 聚合日志输出
  aggregateIntervalMs?: number; // 聚合周期
  // 新增：白名单与匹配精度
  allowList?: {
    exact?: string[]; // 完全匹配白名单
    patterns?: string[]; // 正则白名单（字符串形式）
  };
  englishWordBoundary?: boolean; // 英文词使用词边界
  // 新增：打分与阈值
  riskLevels?: {
    low: number;
    medium: number;
    high: number;
  };
  weights?: {
    defaultWordScore?: number;
    defaultPatternScore?: number;
    longWordBonus?: number; // 长词额外加分
    wordLengthBonusThreshold?: number; // 从多长开始算长词
  };
  // 新增：过滤策略
  filterStrategy?: 'mask-equal' | 'mask-fixed' | 'block';
  fixedMask?: string; // mask-fixed 时使用
}

// 获取敏感词配置
export function getSensitiveWordsConfig(): string[] {
  return sensitiveConfig.words;
}

// 获取敏感模式配置
export function getSensitivePatternsConfig(): RegExp[] {
  // patterns 为 JSON 对象数组，需转为 RegExp
  const basePatterns = (sensitiveConfig.patterns || []).map(
    (p: any) => new RegExp(p.regex, p.flags)
  );
  return basePatterns;
}

export function getFilterConfig(): FilterConfig {
  return {
    enabled: sensitiveConfig.config.enabled ?? true,
    maskChar: sensitiveConfig.config.maskChar ?? '*',
    strictMode: sensitiveConfig.config.strictMode ?? false,
    logSensitive: sensitiveConfig.config.logSensitive ?? false,
    filterUserOnly: sensitiveConfig.config.filterUserOnly ?? true,
    logThrottleMs: 1000,
    logOnlyFinal: true,
    aggregateSensitiveLogs: true,
    aggregateIntervalMs: 5000,
    allowList: {
      exact: [],
      patterns: [],
    },
    englishWordBoundary: true,
    riskLevels: {
      low: 1,
      medium: 3,
      high: 5,
    },
    weights: {
      defaultWordScore: 1,
      defaultPatternScore: 2,
      longWordBonus: 0.5,
      wordLengthBonusThreshold: 4,
    },
    filterStrategy: 'mask-equal',
    fixedMask: '***',
  };
}

// 获取联想问题配置
export function getSuggestionConfig(): SuggestionQuestion[] {
  return suggestionConfig.questions || [];
}

// 初始化配置（应用启动时调用）
export function initContentConfig(): void {
  // 初始化敏感词检测
  const words = getSensitiveWordsConfig();
  const patterns = getSensitivePatternsConfig();
  const filterConfig = getFilterConfig();

  logger.info('[ContentConfig] 内容配置初始化完成', {
    sensitiveWordsCount: words.length,
    sensitivePatternsCount: patterns.length,
    filterEnabled: filterConfig.enabled,
  });
}
